import { Link, useLocation } from "react-router-dom";
import { KeyRound, LogOut, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { useState } from "react";
import type { NavItem } from "@/types/site-config";

const DEFAULT_NAV_LINKS: NavItem[] = [
  { href: "/", label: "Home" },
  { href: "/galleries", label: "Photos" },
  { href: "/writing", label: "Writing" },
  { href: "/links", label: "Links" },
  { href: "/audio", label: "Audio" },
  { href: "/archive", label: "Archive" },
  { href: "/about", label: "About" },
];

function getNavLinkClass(pathname: string, href: string): string {
  return cn(
    "relative font-mono text-sm font-medium transition-colors hover:text-link",
    pathname === href
      ? "text-link after:absolute after:bottom-[-1.25rem] after:left-0 after:right-0 after:h-0.5 after:bg-link"
      : "text-secondary"
  );
}

function getAdminLinkClass(pathname: string): string {
  return cn(
    "relative font-mono text-sm font-medium transition-colors hover:text-link",
    pathname.startsWith("/admin")
      ? "text-link after:absolute after:bottom-[-1.25rem] after:left-0 after:right-0 after:h-0.5 after:bg-link"
      : "text-secondary"
  );
}

function getMobileNavLinkClass(pathname: string, href: string): string {
  return cn(
    "rounded px-4 py-3 font-mono text-base font-medium transition-colors",
    pathname === href
      ? "bg-link/10 text-link"
      : "text-secondary hover:bg-secondary/20 hover:text-link"
  );
}

function getMobileAdminLinkClass(pathname: string): string {
  return cn(
    "rounded px-4 py-3 font-mono text-base font-medium transition-colors",
    pathname.startsWith("/admin")
      ? "bg-link/10 text-link"
      : "text-secondary hover:bg-secondary/20 hover:text-link"
  );
}

function AuthIcon({ isAuthenticated }: { isAuthenticated: boolean }) {
  return isAuthenticated ? (
    <LogOut className="h-5 w-5" />
  ) : (
    <KeyRound className="h-5 w-5" />
  );
}

async function clearFlaskJwt(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST' });
  } catch (error) {
    console.error('Failed to clear Flask JWT:', error);
  }
}

async function submitAuthForm(action: 'signin' | 'signout'): Promise<void> {
  const csrfRes = await fetch('/api/auth/csrf');
  const { csrfToken } = await csrfRes.json();

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action === 'signin' ? '/api/auth/signin/authentik' : '/api/auth/signout';

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'csrfToken';
  input.value = csrfToken;
  form.appendChild(input);

  const callbackInput = document.createElement('input');
  callbackInput.type = 'hidden';
  callbackInput.name = 'callbackUrl';
  callbackInput.value = '/';
  form.appendChild(callbackInput);

  document.body.appendChild(form);
  form.submit();
}

function getAuthLabel(isAuthenticated: boolean): string {
  return isAuthenticated ? "Sign out" : "Sign in";
}

function getAuthAction(isAuthenticated: boolean): 'signin' | 'signout' {
  return isAuthenticated ? 'signout' : 'signin';
}

function DesktopNav({ pathname, isAuthenticated, isLoading, onAuthAction, navLinks }: { pathname: string; isAuthenticated: boolean; isLoading: boolean; onAuthAction: (action: 'signin' | 'signout') => void; navLinks: NavItem[] }) {
  return (
    <nav className="hidden items-center gap-8 md:flex">
      {navLinks.map((link) => (
        <Link key={link.href} to={link.href} className={getNavLinkClass(pathname, link.href)}>
          {link.label}
        </Link>
      ))}
      {isAuthenticated && (
        <Link to="/admin" className={getAdminLinkClass(pathname)}>Admin</Link>
      )}
      <div className="h-6 w-px bg-border" />
      <ThemeSwitcher />
      {!isLoading && (
        <button
          onClick={() => onAuthAction(getAuthAction(isAuthenticated))}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          title={getAuthLabel(isAuthenticated)}
          aria-label={getAuthLabel(isAuthenticated)}
        >
          <AuthIcon isAuthenticated={isAuthenticated} />
        </button>
      )}
    </nav>
  );
}

function MobileNav({ pathname, isAuthenticated, isLoading, onClose, onAuthAction, navLinks }: { pathname: string; isAuthenticated: boolean; isLoading: boolean; onClose: () => void; onAuthAction: (action: 'signin' | 'signout') => void; navLinks: NavItem[] }) {
  return (
    <div className="border-t border-divider bg-background md:hidden">
      <nav className="container flex flex-col space-y-1 py-4">
        {navLinks.map((link) => (
          <Link key={link.href} to={link.href} onClick={onClose} className={getMobileNavLinkClass(pathname, link.href)}>
            {link.label}
          </Link>
        ))}
        {isAuthenticated && (
          <Link to="/admin" onClick={onClose} className={getMobileAdminLinkClass(pathname)}>Admin</Link>
        )}
        <div className="pt-2">
          <ThemeSwitcher />
        </div>
        {!isLoading && (
          <div className="pt-2">
            <button
              onClick={() => onAuthAction(getAuthAction(isAuthenticated))}
              className="flex w-full items-center justify-center rounded border-2 border-foreground bg-transparent px-4 py-3 font-mono text-sm uppercase tracking-wider text-foreground transition-all hover:bg-foreground/10 min-h-[48px]"
              title={getAuthLabel(isAuthenticated)}
              aria-label={getAuthLabel(isAuthenticated)}
            >
              <AuthIcon isAuthenticated={isAuthenticated} />
            </button>
          </div>
        )}
      </nav>
    </div>
  );
}

function resolveNavLinks(config: ReturnType<typeof useSiteConfig>['data']): NavItem[] {
  return config?.navigation ?? DEFAULT_NAV_LINKS;
}

/** The Clint Ecker mark, inlined so it inherits the themed foreground colour
 *  via currentColor (an <img> can't). The ring "ticks" (stepped rotation, see
 *  .ce-mark-tick); the centre dot stays put. Matches public/brand/clint-mark.svg. */
const BrandMark = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 64 64" fill="none" aria-hidden="true" className={className}>
    <g className="ce-mark__advance">
      <g className="ce-mark__settle">
        <circle cx="32" cy="32" r="22" fill="none" stroke="currentColor" strokeWidth="10" strokeDasharray="100 39" transform="rotate(-14 32 32)" />
      </g>
    </g>
    <circle cx="32" cy="32" r="5" fill="currentColor" />
  </svg>
);

const Header = () => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { data: config } = useSiteConfig();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = resolveNavLinks(config);

  const handleAuthAction = async (action: 'signin' | 'signout') => {
    setMobileMenuOpen(false);
    if (action === 'signout') {
      await clearFlaskJwt();
    }
    await submitAuthForm(action);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-divider bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between gap-4">
        <Link to="/" className="group flex shrink-0 items-center gap-3 transition-opacity hover:opacity-90" aria-label="Home">
          <BrandMark className="h-10 w-10 text-foreground" />
          {config?.branding?.showSiteTitle === true && config?.site?.name && (
            <span className="hidden font-serif text-2xl font-semibold leading-none text-foreground sm:inline">
              {config.site.name}
            </span>
          )}
        </Link>
        <DesktopNav pathname={location.pathname} isAuthenticated={isAuthenticated} isLoading={isLoading} onAuthAction={handleAuthAction} navLinks={navLinks} />
        <button
          className="flex items-center justify-center p-2 text-foreground transition-colors hover:text-link md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {mobileMenuOpen && (
        <MobileNav pathname={location.pathname} isAuthenticated={isAuthenticated} isLoading={isLoading} onClose={() => setMobileMenuOpen(false)} onAuthAction={handleAuthAction} navLinks={navLinks} />
      )}
    </header>
  );
};

export default Header;
